import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatPercent } from './radarUtils';

const RadarLucroHome = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cards, setCards] = useState(null);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/radar-lucro/dashboard/summary');
      setCards(data.cards || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar Radar de Lucro');
    } finally {
      setLoading(false);
    }
  };

  const syncLegacy = async () => {
    try {
      setSyncing(true);
      await axios.post('/api/radar-lucro/sync-legado');
      toast.success('Sincronizacao concluida');
      await loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro na sincronizacao');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const resumoTopo = useMemo(() => {
    const risco = cards?.dinheiro_em_risco?.valor || 0;
    const parado = cards?.capital_parado?.valor || 0;
    const margem = cards?.margem_em_perigo?.impacto_30d || 0;
    const total = risco + parado + margem;
    return total;
  }, [cards]);

  return (
    <div className="animate-fade-in space-y-6">
      <div className="rounded-xl border border-danger-200 bg-danger-50 p-4">
        <p className="text-sm font-semibold text-danger-800">Visao executiva de impacto</p>
        <p className="text-2xl font-bold text-danger-900 mt-1">{formatCurrency(resumoTopo)} em risco ou ineficiencia</p>
        <p className="text-sm text-danger-800 mt-2">
          Voce pode estar deixando dinheiro na mesa. Priorize as acoes de hoje para proteger caixa e margem.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Radar de Lucro</h1>
          <p className="text-gray-600">Mostrando dinheiro e consequencia antes das metricas tecnicas</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={loadSummary} disabled={loading}>
            Atualizar
          </button>
          <button className="btn-primary" onClick={syncLegacy} disabled={syncing}>
            {syncing ? 'Sincronizando...' : 'Sincronizar Base'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body text-gray-600">Carregando indicadores...</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="card border-danger-200">
              <div className="card-body">
                <p className="text-sm text-gray-500">Dinheiro em risco (ruptura 7 dias)</p>
                <p className="text-3xl font-bold text-danger-700">{formatCurrency(cards?.dinheiro_em_risco?.valor)}</p>
                <p className="text-sm text-danger-800 mt-2">Consequence: vendas podem nao acontecer nos proximos dias.</p>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li>- {cards?.dinheiro_em_risco?.produtos_criticos || 0} produtos criticos</li>
                  <li>- {cards?.dinheiro_em_risco?.produtos_alerta || 0} em alerta</li>
                  <li>- Priorize reposicao dos maiores riscos</li>
                </ul>
                <Link to="/radar-lucro/ruptura" className="btn-primary mt-3 inline-flex">
                  Ver e agir agora
                </Link>
              </div>
            </div>

            <div className="card border-warning-200">
              <div className="card-body">
                <p className="text-sm text-gray-500">Capital parado</p>
                <p className="text-3xl font-bold text-warning-700">{formatCurrency(cards?.capital_parado?.valor)}</p>
                <p className="text-sm text-warning-800 mt-2">Consequence: caixa preso em estoque sem retorno.</p>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li>- {cards?.capital_parado?.itens || 0} itens com giro baixo</li>
                  <li>- Excesso de cobertura consumindo caixa</li>
                  <li>- Reduza compras dos itens parados</li>
                </ul>
                <Link to="/radar-lucro/capital-parado" className="btn-primary mt-3 inline-flex">
                  Ver onde destravar
                </Link>
              </div>
            </div>

            <div className="card border-danger-200">
              <div className="card-body">
                <p className="text-sm text-gray-500">Margem em perigo</p>
                <p className="text-3xl font-bold text-danger-700">{cards?.margem_em_perigo?.itens_criticos || 0} criticos</p>
                <p className="text-sm text-danger-800 mt-2">
                  Consequence: perda estimada de {formatCurrency(cards?.margem_em_perigo?.impacto_30d)} em 30 dias.
                </p>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li>- Preco zerado ou margem abaixo de 5%</li>
                  <li>- {cards?.margem_em_perigo?.itens_alerta || 0} itens em alerta</li>
                  <li>- Ajuste preco antes de ampliar o prejuizo</li>
                </ul>
                <Link to="/radar-lucro/margem" className="btn-primary mt-3 inline-flex">
                  Corrigir margens
                </Link>
              </div>
            </div>

            <div className="card border-primary-200">
              <div className="card-body">
                <p className="text-sm text-gray-500">Saude do estoque</p>
                <p className="text-3xl font-bold text-primary-700">{formatPercent(cards?.saude_estoque?.confiabilidade_pct)}</p>
                <p className="text-sm text-primary-800 mt-2">Consequence: dados inconsistentes podem distorcer decisoes.</p>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li>- Negativo: {cards?.saude_estoque?.itens_estoque_negativo || 0}</li>
                  <li>- Sem preco: {cards?.saude_estoque?.itens_sem_preco || 0}</li>
                  <li>- Sem custo: {cards?.saude_estoque?.itens_sem_custo || 0}</li>
                </ul>
                <Link to="/radar-lucro/saude-estoque" className="btn-primary mt-3 inline-flex">
                  Corrigir inconsistencias
                </Link>
              </div>
            </div>

            <div className="card border-warning-200">
              <div className="card-body">
                <p className="text-sm text-gray-500">Produtos que pararam de vender</p>
                <p className="text-3xl font-bold text-warning-700">{formatCurrency(cards?.queda_giro?.valor_estoque_travado)}</p>
                <p className="text-sm text-warning-800 mt-2">Voce pode estar acumulando capital em itens que perderam giro.</p>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li>- {cards?.queda_giro?.itens || 0} itens sem venda recente</li>
                  <li>- Janela padrao: {cards?.queda_giro?.dias || 15} dias</li>
                  <li>- Foque primeiro no maior estoque travado</li>
                </ul>
                <Link to="/radar-lucro/queda-giro" className="btn-primary mt-3 inline-flex">
                  Ver Queda de Giro
                </Link>
              </div>
            </div>

            <div className="card md:col-span-2 border-success-200">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Acao recomendada hoje</p>
                  <Link to="/radar-lucro/acoes" className="btn-primary">
                    Abrir plano de acao
                  </Link>
                </div>
                <p className="text-sm text-success-800 mt-2">
                  Direcionamento objetivo para proteger receita, margem e caixa ainda hoje.
                </p>
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  {(cards?.acoes_recomendadas_hoje?.itens || []).slice(0, 6).map((acao, index) => (
                    <li key={`${acao.tipo}-${acao.id_produto}-${index}`} className="border-b border-gray-100 pb-2">
                      <span className="font-semibold">{acao.descricao}</span>
                      <span className="text-gray-500"> - {acao.motivo}</span>
                    </li>
                  ))}
                  {(cards?.acoes_recomendadas_hoje?.itens || []).length === 0 && (
                    <li>Nenhuma acao critica agora. Mantenha o monitoramento diario.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-gray-900">Comparar periodos</p>
                <p className="text-sm text-gray-600">Ultimos 28 dias versus 28 dias anteriores com setas de variacao</p>
              </div>
              <Link to="/radar-lucro/comparativo" className="btn-secondary">
                Abrir comparativo
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RadarLucroHome;
